import { useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Check, Crown, Sparkles } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { usePlans } from '../../hooks/usePlans';
import type { PricingPlan } from '../../api/plans';
import posthog from 'posthog-js';
import { loadStripe } from '@stripe/stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { toast } from 'sonner';

const faqItems = [
  {
    question: 'Can I switch plans later?',
    answer:
      'Yes. Upgrade or downgrade at any time in the billing portal. Credits are prorated automatically when you move between plans.',
  },
  {
    question: 'Do plans include Lighthouse talent services?',
    answer:
      'Growth and Enterprise include access to Lighthouse talent advocates, analytics, and AI tools. Starter customers can purchase add-ons as needed.',
  },
  {
    question: 'Is there a minimum contract term?',
    answer: 'Monthly plans renew automatically. Enterprise customers can opt for annual contracts with additional discounts.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

function PlanSkeleton() {
  return (
    <Card className="border border-white/10 bg-white/[0.02]">
      <CardHeader className="space-y-4 text-center">
        <div className="mx-auto h-4 w-24 animate-pulse rounded bg-white/10" />
        <div className="mx-auto h-8 w-32 animate-pulse rounded bg-white/10" />
        <div className="mx-auto h-3 w-44 animate-pulse rounded bg-white/10" />
      </CardHeader>
      <CardContent className="space-y-4 p-6 pt-0">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={`plan-skeleton-${index}`} className="h-3 w-full animate-pulse rounded bg-white/10" />
        ))}
        <div className="h-10 w-full animate-pulse rounded bg-white/10" />
      </CardContent>
    </Card>
  );
}

type StripeCheckoutClient = {
  redirectToCheckout: (options: {
    mode: 'subscription';
    lineItems: Array<{ price: string; quantity: number }>;
    successUrl: string;
    cancelUrl: string;
  }) => Promise<{ error?: { message?: string } }>;
};

async function redirectToStripe(priceId: string) {
  const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
  if (!publishableKey) {
    throw new Error('Stripe publishable key missing');
  }
  const stripe = (await loadStripe(publishableKey)) as (Stripe & StripeCheckoutClient) | null;
  if (!stripe) throw new Error('Unable to init Stripe');
  return stripe.redirectToCheckout({
    mode: 'subscription',
    lineItems: [{ price: priceId, quantity: 1 }],
    successUrl: window.location.origin + '/pricing?success=true',
    cancelUrl: window.location.origin + '/pricing?cancelled=true',
  });
}

function PlanCard({ plan }: { plan: PricingPlan }) {
  const accent = plan.accent ?? 'border-white/10 bg-white/[0.03]';
  const handleClick = async () => {
    posthog.capture('pricing_cta_click', { plan: plan.analyticsKey ?? plan.name });
    if (plan.stripePriceId) {
      try {
        const result = await redirectToStripe(plan.stripePriceId);
        if (result.error) {
          throw new Error(result.error.message);
        }
        return;
      } catch (stripeError) {
        const message = stripeError instanceof Error ? stripeError.message : 'Unable to start checkout';
        posthog.capture('pricing_checkout_error', { plan: plan.analyticsKey ?? plan.name, message });
        toast.error(message);
        return;
      }
    }
    if (plan.ctaLink) {
      window.open(plan.ctaLink, '_blank', 'noopener');
      return;
    }
    toast.info('A Lighthouse advocate will reach out to schedule next steps.');
  };
  return (
    <Card className={`relative border ${accent} transition hover:-translate-y-1 hover:border-[#d2ac47]/40`}>
      {plan.popular && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold-gradient text-black shadow-md shadow-[#d2ac47]/35">
          <Crown className="mr-1 h-3 w-3" aria-hidden />
          Most popular
        </Badge>
      )}
      <CardHeader className="space-y-4 text-center">
        <CardTitle className="text-2xl text-white">{plan.name}</CardTitle>
        <div>
          <span className="text-4xl font-semibold text-white">{plan.price}</span>
          <span className="ml-2 text-sm text-gray-400">{plan.cadence}</span>
        </div>
        <CardDescription className="text-sm text-gray-400">{plan.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 p-6 pt-0">
        <ul className="space-y-3 text-sm text-gray-200">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-3">
              <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#ba9036]/20 text-[#f7ef8a]">
                <Check className="h-3 w-3" aria-hidden />
              </span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <Button
          className={`w-full ${
            plan.popular ? 'bg-gold-gradient text-black hover:brightness-110' : 'bg-white/10 text-white hover:bg-white/20'
          }`}
          onClick={handleClick}
        >
          {plan.cta}
        </Button>
      </CardContent>
    </Card>
  );
}

export function PricingPage() {
  const { data: plans, isPending, isError, error, refetch } = usePlans();

  useEffect(() => {
    if (plans?.length) {
      posthog.capture('pricing_plans_loaded', { count: plans.length });
    }
  }, [plans]);

  return (
    <main className="min-h-screen bg-[#080a18] pb-24" aria-labelledby="pricing-heading">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <header className="border-b border-white/10 bg-[#0d1022]/80 backdrop-blur-sm">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#d2ac47]/30 bg-[#ba9036]/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-[#f7ef8a]">
            Pricing that scales
          </div>
          <h1 id="pricing-heading" className="mt-6 text-4xl font-semibold text-white sm:text-5xl">
            Plans designed for inclusive hiring journeys
          </h1>
          <p className="mt-4 text-lg text-gray-300 sm:text-xl">
            Start free, then upgrade when you need AI co-pilots, advanced analytics, and Lighthouse guidance.
          </p>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-4 pt-14 sm:px-6 lg:px-8" aria-label="Pricing plans and FAQs">
        {isError && (
          <div className="mb-8 rounded-3xl border border-red-500/40 bg-red-500/10 p-6 text-center text-sm text-red-100">
            <p>{error.message}</p>
            <Button variant="outline" className="mt-3 border-red-400 text-red-100 hover:bg-red-500/20" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-3">
          {isPending && Array.from({ length: 3 }).map((_, index) => <PlanSkeleton key={`plan-skeleton-${index}`} />)}
          {!isPending && plans?.map((plan) => <PlanCard key={plan.name} plan={plan} />)}
        </div>

        <section className="mt-16 rounded-3xl border border-white/10 bg-white/[0.03] p-12 text-center">
          <div className="mx-auto max-w-3xl space-y-4">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#d2ac47]/40 bg-[#ba9036]/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-[#f7ef8a]">
              Returner success
            </span>
            <h2 className="text-3xl font-semibold text-white sm:text-4xl">
              95% of Lighthouse returners secure interviews within 6 weeks.
            </h2>
            <p className="text-sm text-gray-300">
              Our talent advocates partner with you to craft onboarding, mentoring, and team readiness playbooks. Add Lighthouse Pro
              services to any paid plan.
            </p>
            <Button variant="outline" className="border-[#d2ac47]/50 text-[#f7ef8a] hover:bg-[#ba9036]/10">
              Speak to the partnerships team
            </Button>
          </div>
        </section>

        <section className="mt-16 rounded-3xl border border-white/10 bg-white/[0.02] p-8 sm:p-12">
          <div className="mx-auto max-w-4xl space-y-6 text-left">
            <h3 className="text-2xl font-semibold text-white">Pricing FAQs</h3>
            <div className="space-y-4">
              {faqItems.map((item) => (
                <details key={item.question} className="rounded-2xl border border-white/10 bg-white/[0.01] px-4 py-3 text-sm text-gray-200">
                  <summary className="cursor-pointer text-base text-white">{item.question}</summary>
                  <p className="mt-2 text-gray-300">{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </section>
    </main>
  );
}
